import {
  Activity,
  AlertTriangle,
  BarChart3,
  BookOpen,
  Clock3,
  CreditCard,
  Database,
  DollarSign,
  Globe,
  LayoutDashboard,
  Mail,
  Network,
  Radio,
  Receipt,
  Router,
  Settings,
  ShieldCheck,
  Smartphone,
  Ticket,
  UserCog,
  Users,
  Wallet,
  Wifi,
  Wrench,
  Headphones,
} from "lucide-react";

import type { MenuItem } from "../types";

const admins = ["admin"];
const superAdmins = ["super admin"];
const customerCare = ["customer_care"];

export const menuItems: MenuItem[] = [
  // ================= DASHBOARD =================
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/overview",
    group: "Dashboard",
    allowedRoles: admins,
  },
  {
    id: "network-overview",
    label: "Network Overview",
    icon: Activity,
    path: "/overview/mikrotik",
    group: "Dashboard",
    allowedRoles: admins,
  },

  // ================= CUSTOMERS =================
  {
    id: "pppoe-customers",
    label: "PPPoE Customers",
    icon: Users,
    path: "/customers",
    group: "Customers",
    allowedRoles: admins,
  },
  {
    id: "active-connections",
    label: "Active Sessions",
    icon: Wifi,
    path: "/customers/active-connections",
    group: "Customers",
    allowedRoles: admins,
  },
  {
    id: "billing-groups",
    label: "Billing Groups",
    icon: Wallet,
    path: "/pppoe-billing-groups",
    group: "Customers",
    allowedRoles: admins,
  },
  {
    id: "hotspot-customers",
    label: "Hotspot Users",
    icon: Radio,
    path: "/hotspot",
    group: "Customers",
    allowedRoles: admins,
  },
  {
    id: "expiring-customers",
    label: "Expiring Accounts",
    icon: AlertTriangle,
    path: "/customers/expiring",
    group: "Customers",
    allowedRoles: admins,
  },
  {
    id: "extended-access",
    label: "Extended Access",
    icon: Clock3,
    path: "/customers/extended",
    group: "Customers",
    allowedRoles: admins,
  },

  // ================= SERVICES =================
  {
    id: "internet-plans",
    label: "Internet Plans",
    icon: Globe,
    path: "/plans",
    group: "Services",
    allowedRoles: admins,
  },
  {
    id: "hotspot-plans",
    label: "Hotspot Plans",
    icon: Wifi,
    path: "/hotspot/plans",
    group: "Services",
    allowedRoles: admins,
  },

  // ================= FINANCE =================
  {
    id: "pppoe-finance",
    label: "PPPoE Billing",
    icon: CreditCard,
    group: "Finance",
    allowedRoles: admins,
    children: [
      {
        id: "pppoe-invoices",
        label: "Invoices",
        path: "/finance/invoices",
      },
      {
        id: "pppoe-payments",
        label: "Payments",
        path: "/finance/payments",
      },
      {
        id: "mpesa-logs",
        label: "M-Pesa Transactions",
        path: "/finance/mpesa",
      },

      {
        id: "failed-transactions",
        label: "Failed Transactions",
        path: "/finance/error",
      },

      {
        id: "ncba-logs",
        label: "NCBA Transactions",
        path: "/finance/ncba",
      },
    ],
  },

  {
    id: "hotspot-finance",
    label: "Hotspot Billing",
    icon: DollarSign,
    group: "Finance",
    allowedRoles: admins,
    children: [
      {
        id: "hotspot-payments",
        label: "Payments",
        path: "/finance/hotspot/payments",
      },
    ],
  },

  {
    id: "general-finance",
    label: "Finance Management",
    icon: Receipt,
    group: "Finance",
    allowedRoles: admins,
    children: [
      {
        id: "expenses",
        label: "Expenses",
        path: "/finance/expenses",
      },
    ],
  },

  // ================= PARTNERSHIP =================
  {
    id: "reseller-partners",
    label: "Reseller Partners",
    icon: UserCog,
    path: "/reseller-partners",
    group: "Partnership",
    allowedRoles: admins,
    allowedUsers: [
      "df0a7017-592a-4468-ac2e-52873ede024b",
      "d0174f85-b4c9-4a36-832f-540f8f624616",
      "131ac96d-8088-419c-8649-5a90a60b1ebd",
    ],
  },

  // ================= SUPPORT =================
  {
    id: "technicians",
    label: "Technicians",
    icon: Wrench,
    path: "/technicians",
    group: "Support",
    allowedRoles: admins,
  },

  {
    id: "support-tickets",
    label: "Support Tickets",
    icon: Ticket,
    path: "/support/tickets",
    group: "Support",
    allowedRoles: admins,
  },

  {
    id: "customer-care-users",
    label: "Customer Care Users",
    icon: Headphones,
    path: "/support/customer-care-users",
    group: "Support",
    allowedRoles: admins,
  },

  // ================= NETWORK =================
  {
    id: "network-management",
    label: "Network Management",
    icon: Network,
    group: "Network",
    allowedRoles: admins,
    children: [
      {
        id: "interfaces",
        label: "Interfaces",
        path: "/interfaces",
      },
      {
        id: "bridges",
        label: "Bridges",
        path: "/bridges",
      },
      {
        id: "ip-pools",
        label: "IP Pools",
        path: "/pools",
      },
      {
        id: "pppoe-server",
        label: "PPPoE Server",
        path: "/pppoe",
      },
      {
        id: "ip-addresses",
        label: "IP Addresses",
        path: "/ip-address",
      },
    ],
  },

  {
    id: "mikrotik-tools",
    label: "MikroTik Tools",
    icon: Router,
    group: "Network",
    allowedRoles: admins,
    children: [
      {
        id: "terminal",
        label: "Terminal",
        path: "/terminal",
      },
      {
        id: "terminal-logs",
        label: "Terminal Logs",
        path: "/terminal/logs",
      },
      {
        id: "backup",
        label: "Backups",
        path: "/mikrotik/backup",
      },
    ],
  },

  // ================= COMMUNICATION =================
  {
    id: "sms",
    label: "SMS Center",
    icon: Smartphone,
    path: "/sms",
    group: "Communication",
    allowedRoles: admins,
  },

  {
    id: "sms-templates",
    label: "SMS Templates",
    icon: Mail,
    path: "/sms/templates",
    group: "Communication",
    allowedRoles: admins,
  },

  {
    id: "email",
    label: "Email Center",
    icon: Mail,
    path: "/email",
    group: "Communication",
    allowedRoles: admins,
  },

  // ================= REPORTS =================
  {
    id: "reports",
    label: "Reports & Analytics",
    icon: BarChart3,
    path: "/timely-report",
    group: "Reports",
    allowedRoles: admins,
  },

  // ================= ADMINISTRATION =================
  {
    id: "client-management",
    label: "Client Management",
    icon: UserCog,
    path: "/clients",
    group: "Administration",
    allowedRoles: superAdmins,
  },
  {
    id: "router-management",
    label: "Router Management",
    icon: UserCog,
    path: "/routers",
    group: "Administration",
    allowedRoles: superAdmins,
  },

  {
    id: "system-settings",
    label: "System Settings",
    icon: Settings,
    group: "Administration",
    allowedRoles: superAdmins,
    children: [
      {
        id: "roles",
        label: "User Roles",
        path: "/roles",
      },
      {
        id: "general-settings",
        label: "General Settings",
        path: "/general",
      },
      {
        id: "domains",
        label: "Domains",
        path: "/domains",
      },
      {
        id: "integrations",
        label: "Integrations",
        path: "/integrations",
      },
      {
        id: "billing-config",
        label: "Billing Configuration",
        path: "/billing",
      },
      {
        id: "payouts",
        label: "Payout Settings",
        path: "/payouts",
      },
    ],
  },

  // ================= SYSTEM =================
  {
    id: "system-tools",
    label: "System Tools",
    icon: Database,
    group: "System",
    allowedRoles: superAdmins,
    children: [
      {
        id: "server-management",
        label: "Server Management",
        path: "/server-management",
      },
      {
        id: "audit-logs",
        label: "Audit Logs",
        path: "/audit-logs",
      },
    ],
  },

  // ================= HELP =================
  {
    id: "documentation",
    label: "Documentation",
    icon: BookOpen,
    path: "/documentation",
    group: "Help Center",
    allowedRoles: admins,
  },

  {
    id: "security",
    label: "Security",
    icon: ShieldCheck,
    path: "/security",
    group: "Help Center",
    allowedRoles: superAdmins,
  },
];
